import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CONNECT_STATUS, REGISTER_STATUS, useSIPProvider } from "../helper/sipProvider";

// Lucide Icons
import {
  Menu,
  Sun,
  Moon,
  Phone,
  User,
  LogOut,
  ChevronDown,
  Wifi,
  WifiOff
} from "lucide-react";

// Local Imports
import {
  BASE_API,
  DecryptToken,
  buildAuthenticatedEventSourceUrl,
} from "../helper/Constants";

const brandLogo = `${process.env.PUBLIC_URL || ""}/callmafia-logo.png`;

const Header = ({ navHandler }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // SIP Context
  const { connectStatus, registerStatus } = useSIPProvider();

  // Constants
  const API_SERVER_URL = window.CAMPAIN_WWS_URL || BASE_API;

  /* ---------------- State ---------------- */
  const [userEmail, setUserEmail] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [callCounts, setCallCounts] = useState({
    campaignCallsCount: 0,
    extensionCallsCount: 0,
  });

  // Dark Mode State with Persistence (Checks local storage first)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  /* ---------------- Effects ---------------- */

  // 1. Handle Dark Mode Toggle & Persistence
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  // 2. Click Outside Dropdown to Close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 3. SSE for Live Calls
  useEffect(() => {
    if (!API_SERVER_URL) return;

    const eventSourceUrl = buildAuthenticatedEventSourceUrl("api/stream/admin");
    if (!eventSourceUrl) return;

    const eventSource = new EventSource(eventSourceUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "update") {
          setCallCounts(data.data.counts);
        }
      } catch (err) {
        console.error("Error parsing SSE data:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.warn("SSE Error:", err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [API_SERVER_URL]);

  // 4. Fetch User Info
  useEffect(() => {
    const fetchUserEmail = () => {
      try {
        const userToken = localStorage.getItem("psx_token");
        if (userToken) {
          const decodedToken = DecryptToken(userToken);
          if (decodedToken) {
            setUserEmail(decodedToken.email);
            // setUserId(decodedToken.user_id); // Uncomment if used later
          }
        }
      } catch (error) {
        console.error("Error fetching user email:", error);
      }
    };
    fetchUserEmail();
  }, []);

  /* ---------------- Handlers ---------------- */

  const handleLogout = () => {
    const userToken = localStorage.getItem("psx_token");
    if (userToken) {
      localStorage.removeItem("psx_token");
      navigate("/", { replace: true });
      toast.success("Logged out successfully");
    }
  };

  return (
    <header className="fixed top-0 z-50 flex h-16 w-full items-center justify-between border-b border-red-100 bg-white/95 px-3 shadow-sm backdrop-blur-md transition-all duration-300 sm:px-4 lg:px-6 dark:border-red-950/40 dark:bg-black/95">

      {/* --- Left Section: Logo & Toggle --- */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        {/* Mobile Sidebar Toggle */}
        <button
          onClick={navHandler}
          className="rounded-xl p-2 text-zinc-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 dark:text-zinc-300 dark:hover:bg-red-950/30 dark:focus:ring-red-950/40"
          aria-label="Toggle Sidebar"
        >
          <Menu size={24} />
        </button>

        {/* Logo */}
        <Link to="/dashboard" className="flex min-w-0 items-center gap-3 decoration-transparent">
          <div className="overflow-hidden rounded-2xl border border-red-200 bg-white shadow-[0_12px_30px_rgba(127,29,29,0.16)] dark:border-red-900/40 dark:bg-neutral-950 dark:shadow-[0_14px_32px_rgba(127,29,29,0.32)]">
            <img
              src={brandLogo}
              alt="CallMafia logo"
              className="h-10 w-10 object-cover"
            />
          </div>
          <div className="flex min-w-0 flex-col leading-none">
            <span className="truncate text-lg font-extrabold tracking-[0.18em] sm:text-xl">
              <span className="text-zinc-950 dark:text-white">CALL</span>
              <span className="ml-2 text-red-700 dark:text-red-500">MAFIA</span>
            </span>
            <span className="hidden text-[11px] font-medium uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400 sm:block">
              Admin
            </span>
          </div>
        </Link>

        {/* SIP Status Badge (Desktop) */}
        <div className={`hidden md:flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
          (connectStatus === CONNECT_STATUS.CONNECTED && registerStatus === REGISTER_STATUS.REGISTERED)
            ? "border-red-700 bg-red-700 text-white dark:border-red-600 dark:bg-red-600 dark:text-white"
            : "border-zinc-200 bg-zinc-100 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300"
        }`}>
          {(connectStatus === CONNECT_STATUS.CONNECTED && registerStatus === REGISTER_STATUS.REGISTERED) ? (
            <Wifi size={14} className="animate-pulse" />
          ) : (
            <WifiOff size={14} />
          )}
          <span>{(connectStatus === CONNECT_STATUS.CONNECTED && registerStatus === REGISTER_STATUS.REGISTERED) ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {/* --- Right Section: Stats, Theme, Profile --- */}
      <div className="flex items-center gap-2 sm:gap-3 md:gap-5">

        {/* 1. Live Call Stats (Hidden on Mobile) */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Campaign Calls */}
          {/* <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => renderTooltip(props, "Active Campaign Calls")}
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
              <PhoneCall size={16} className="text-green-600 dark:text-green-400" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                {callCounts.campaignCallsCount} <span className="text-xs font-medium text-slate-500">Camp</span>
              </span>
            </div>
          </OverlayTrigger> */}

          {/* Extension Calls */}
          <div
            title="Active Extension Calls"
            className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-1.5 shadow-sm dark:border-red-950/40 dark:bg-red-950/20"
          >
            <Phone size={16} className="text-red-700 dark:text-red-300" />
            <span className="text-sm font-bold text-red-800 dark:text-red-200">
              {callCounts.extensionCallsCount} <span className="hidden text-xs font-medium text-red-600/80 dark:text-red-300/80 sm:inline">Ext</span>
            </span>
          </div>
        </div>

        {/* 2. Theme Toggle */}
        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="rounded-full p-2 text-zinc-500 transition-all hover:bg-red-50 focus:outline-none dark:text-zinc-400 dark:hover:bg-red-950/30"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <Sun size={20} className="text-red-300" />
          ) : (
            <Moon size={20} className="text-red-700" />
          )}
        </button>

        {/* 3. Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 rounded-full p-1 transition-all hover:bg-red-50 focus:outline-none dark:hover:bg-red-950/30"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-700 font-bold text-white shadow-md ring-2 ring-white dark:bg-red-500 dark:text-white dark:ring-zinc-900">
              {userEmail ? userEmail.charAt(0).toUpperCase() : <User size={18} />}
            </div>

            <div className="hidden md:flex flex-col items-start">
               {userEmail && (
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                   {userEmail.split('@')[0]}
                </span>
               )}
            </div>
            <ChevronDown
              size={16}
              className={`text-zinc-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 z-50 mt-3 w-60 rounded-2xl border border-red-100 bg-white py-2 shadow-xl duration-200 animate-in fade-in slide-in-from-top-2 dark:border-red-950/40 dark:bg-black">

              {/* Header */}
              <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
                <p className="truncate text-sm font-bold text-zinc-900 dark:text-white">
                  {userEmail || "Guest User"}
                </p>
              </div>

              <Link to="/profile" className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-600 hover:bg-red-50 dark:text-zinc-200 dark:hover:bg-red-950/30">
                <User size={16} />
                Profile
              </Link>

              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-4 py-3 text-sm text-zinc-700 hover:bg-red-50 dark:text-zinc-200 dark:hover:bg-red-950/30"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
